import { Box } from '@repo/ui/Box';
import { Badge } from '@repo/ui/Badge';
import { Button } from '@repo/ui/Button';
import { Flex } from '@repo/ui/Flex';
import { Icon } from '@repo/ui/Icon';
import { Separator } from '@repo/ui/Separator';
import { Instructions } from './InstructionItem';
import s from './RequestDetails.module.css';
import { getContextWidget, getSubmittedItems, getBaseKeys } from './helpers';
import type { RequestDetailsProps } from './types';
import { Text } from '../Language/Language';
import { PackagingOptionProvider } from './PackagingOptionContext';
import { Widget } from './Widgets';

export const RequestDetails = (props: RequestDetailsProps) => {
  const {
    headerTitle,
    headerSubtitle,
    data,
    replacementTokens,
    children,
    pollingStatus,
    confirmationEmail = true,
    keyPrefix = 'Global',
    onBack,
  } = props;

  const {
    requestId,
    label,
    qrCode,
    deliveryMethod,
    deliveryType,
    status,
    location,
    orderItems,
    requestItems,
    email,
    claimStatus,
    claimId,
  } = data;

  const submittedItems = getSubmittedItems({ requestItems, orderItems });

  const contextWidget = getContextWidget({
    status,
    type: deliveryType,
    packaging: deliveryMethod?.packagingOption,
    claimStatus,
    keyPrefix,
  });

  // Hide email widget if confirmationEmail is disabled and widget is email type
  const shouldHideEmailWidget = !confirmationEmail && contextWidget === 'email';
  const showContextWidget = contextWidget && !shouldHideEmailWidget;

  return (
    <PackagingOptionProvider
      initialPackagingOption={deliveryMethod?.packagingOption}
    >
      <Box isContainer>
        <Flex
          direction="column"
          gap={['g-32', 'md:g-48']}
          className={s.container}
        >
          <Flex
            direction="column"
            gap={['g-32', 'md:g-48']}
            padding={['px-24', 'md:px-0']}
          >
            <Flex className={s.wrapper} direction="column" gap="g-16">
              {claimStatus && (
                <Badge type="neutral" tone="subtle" size="sm">
                  <Text langKey={`Global.claimStatus.${claimStatus}`} />
                </Badge>
              )}
              <Flex direction="column" gap="g-4">
                <h1 className={s.title}>
                  <Text {...headerTitle} />
                </h1>
                <h2 className={s.subtitle}>
                  <Text {...headerSubtitle} />
                </h2>
              </Flex>
            </Flex>
            {showContextWidget && (
              <>
                <Separator size="sm" />
                <Widget
                  context={contextWidget}
                  status={status}
                  label={label}
                  qrCode={qrCode}
                  deliveryMethod={deliveryMethod}
                  deliveryType={deliveryType}
                  pollingStatus={pollingStatus}
                  orderItems={submittedItems}
                  rawOrderItems={orderItems}
                  requestItems={requestItems}
                  location={location}
                  email={email}
                  requestId={requestId}
                  claimId={claimId}
                />
              </>
            )}
          </Flex>
          <Flex
            direction="column"
            gap={['g-32', 'md:g-48']}
            padding={['px-24', 'md:px-0']}
          >
            <h3 className={s.instructionsHeader}>
              <Text
                langKey={getBaseKeys({
                  status,
                  type: deliveryType,
                  packaging: deliveryMethod?.packagingOption,
                  claimStatus,
                  keyPrefix,
                }).map((baseKey) => `${baseKey}.header`)}
              />
            </h3>
            <Flex direction="column" gap={'g-32'}>
              <Flex direction="column" gap="g-24">
                <Instructions
                  status={status}
                  deliveryMethod={deliveryMethod}
                  deliveryType={deliveryType}
                  claimStatus={claimStatus}
                  label={label}
                  qrCode={qrCode}
                  pollingStatus={pollingStatus}
                  orderItems={submittedItems}
                  rawOrderItems={orderItems}
                  requestItems={requestItems}
                  replacementTokens={replacementTokens}
                  location={location}
                  email={email}
                  requestId={requestId}
                  keyPrefix={keyPrefix}
                />
              </Flex>
              {children}
              {onBack && (
                <Button variant="secondary" isFullWidth onClick={onBack}>
                  <Icon name="arrow-left" size="sm" />
                  <Text langKey="Global.requestDetails.back" />
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Box>
    </PackagingOptionProvider>
  );
};
